import { useEffect, useMemo, useState } from "react";
import { useParams, useHistory, useLocation } from "react-router-dom";
import clsx from "clsx";
import { Box, Grid, Theme, Typography } from "@mui/material";
import { createStyles, makeStyles } from "@mui/styles";

import { useGiftsGiven, useGlobalGifts } from "JS/React/Hooks/Gifts";
import { useGlobalCourses } from "JS/React/Hooks/Course/Course";
import { AppHeader } from "JS/React/Components/AppHeader";
import {
    AppContentListItem,
    AppContentListItemType,
} from "JS/React/Components/AppContentListItem";
import { AppCircularProgress } from "JS/React/Components/Progress/AppCircularProgress";
import { AllTabs } from "JS/Models";
import { paginateData, PaginationInfo } from "JS/Types/Pagination";
import { useVerifyAwsKeys } from "JS/React/Hooks/Media";
import { getPublicUrl } from "JS/Helpers";
import { useGlobalNavStack } from "JS/React/Hooks/NavStack";
import { useRouting } from "JS/React/Hooks/Routes";
import { sortData } from "JS/Types";
import { messaging } from "JS/Helpers/UserMessaging";
import { Layout } from "../Layout";
import qs from "qs";

export const GiftsGivenContentListing = (props: any) => {
    const params: any = useParams();
    const { memberId, selectedTab, categoryId } = params;
    const Tabs: any = AllTabs;
    const classes = useStyles(props);
    const { linkProvider } = useRouting();
    const { className, ...rest } = props;
    const {
        homeStack,
        giftsStack,
        setGlobalGiftsCategoryStack,
        resetGlobalGiftsCategoryStack,
    } = useGlobalNavStack();
    const [searchQuery, setSearchQuery] = useState<string>(
        giftsStack[categoryId]?.searchQuery
            ? giftsStack[categoryId]?.searchQuery
            : "",
    );
    const { loading, loaded, filteredGifts } = useGiftsGiven(
        "gifts",
        searchQuery,
        memberId,
    );
    const [paginationInfo, setPaginationInfo] = useState<PaginationInfo>({
        currentPage: 1,
        perPage: 10,
        total: 0,
    });
    const [thumbnails, setThumbnails] = useState<{
        [key: string]: string;
    }>(null);
    const history = useHistory();
    const { scrollTo } = useQueryParams();
    const { verifyAwsKeys } = useVerifyAwsKeys();
    const filteredByContentType = useMemo(
        () =>
            filteredGifts.filter((g) => {
                return (
                    (selectedTab === AllTabs.AUDIOS &&
                        g.content_type.includes("MP3")) ||
                    (selectedTab === AllTabs.VIDEOS &&
                        g.content_type.includes("Video")) ||
                    (selectedTab === AllTabs.COURSES &&
                        g.content_type.includes("Course"))
                );
            }),
        [filteredGifts, selectedTab],
    );

    const finalData = useMemo(
        () =>
            paginateData(
                sortData(filteredByContentType, [
                    {
                        col: "title",
                        dir: "asc",
                        position: 0,
                    },
                ]),
                paginationInfo,
            ).data,
        [filteredByContentType, paginationInfo],
    );

    useEffect(() => {
        setPaginationInfo({
            ...paginationInfo,
            total: filteredGifts ? filteredGifts.length : 0,
        });
        setGlobalGiftsCategoryStack(
            {
                searchQuery: searchQuery,
            },
            categoryId,
        );
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filteredGifts]);

    useEffect(() => {
        if (finalData) {
            verifyAwsKeys().then(() => {
                fetchThumbnails();
            });
        }
    }, [finalData]);

    const createImageUrl = async (gift) => {
        if (gift.content_item_type_name === "GiftCourse") {
            const url = await getPublicUrl(
                gift.image.image_url_prefix,
                gift.image.image_url_postfix,
                gift.image.image_file_name,
            );
            return {
                id: gift.nid,
                url: url,
            };
        } else {
            const url = await getPublicUrl(
                gift.image_url_prefix,
                gift.image_url_postfix,
                gift.image_file_name,
            );
            return {
                id: gift.nid,
                url: url,
            };
        }
    };

    const fetchThumbnails = async () => {
        let promises: Promise<{
            id: string;
            url: string;
        }>[] = [];

        finalData.forEach((d) => {
            if (!thumbnails || (thumbnails && !thumbnails[d.nid])) {
                const promise = createImageUrl(d);
                promises.push(promise);
            }
        });

        const thumbs = await Promise.all(promises);

        let toRet: {
            [key: string]: string;
        } = {};

        thumbs.forEach((t) => {
            toRet = {
                ...toRet,
                [t.id]: t.url,
            };
        });

        setThumbnails((prev) => ({
            ...prev,
            ...toRet,
        }));
    };

    let contentItemType: AppContentListItemType = "gift";

    const goBack = () => {
        resetGlobalGiftsCategoryStack(categoryId);
        if (!homeStack?.isHomeTrack) {
            history.push(
                linkProvider.react
                    .gifts()
                    .giftsGivenListing("gifts-given", { scrollTo: +scrollTo }),
            );
        } else {
            history.goBack();
        }
    };

    const setSelectedTab = (tab: AllTabs) => {
        history.replace(
            linkProvider.react
                .gifts()
                .giftsGivenContentListing(memberId, tab, categoryId, {
                    scrollTo: +scrollTo,
                }),
        );
    };

    return (
        <Layout
            paginationInfo={!loading && paginationInfo}
            onLastContentHit={() =>
                setPaginationInfo({
                    ...paginationInfo,
                    perPage: paginationInfo.perPage + 10,
                })
            }
        >
            <div className={clsx(className, classes.root)} {...rest}>
                <AppHeader
                    title={"Gifts"}
                    canGoBack
                    searchable={true}
                    searchQuery={searchQuery}
                    setSearchQuery={setSearchQuery}
                    tabs={Tabs}
                    selectedTab={selectedTab}
                    setSelectedTab={setSelectedTab}
                    showTabs={true}
                    searchPlaceholder={`Search ${selectedTab}s by title or description`}
                    onBackClick={() => {
                        goBack();
                    }}
                />

                {loaded &&
                    finalData?.map((g, idx) => (
                        <AppContentListItem
                            nid={g.nid}
                            type={contentItemType}
                            key={idx}
                            title={g.title}
                            skuId={g.sku_id}
                            description={g.description}
                            imageUrl={thumbnails && thumbnails[g.nid]}
                            isAllowClick
                            onListItemClick={() => {}}
                            releaseDate={g.sending_date}
                            isGiftGiven={true}
                        />
                    ))}

                {loading && (
                    <Grid
                        className={clsx(classes.wrapperLoader)}
                        item={true}
                        xs={12}
                        md={12}
                    >
                        <AppCircularProgress />
                    </Grid>
                )}
                {loaded && !filteredGifts.length && (
                    <Typography
                        align="center"
                        variant="h6"
                        className={classes.noContent}
                    >
                        {messaging.common.noItemsFound}
                    </Typography>
                )}
            </div>
        </Layout>
    );
};

const useQueryParams = () => {
    const location = useLocation();
    const queryParams = useMemo(() => {
        const parsedQueryString = qs.parse(location.search, {
            ignoreQueryPrefix: true,
        });
        const { scrollTo = "0" } = parsedQueryString;

        return {
            scrollTo,
        };
    }, [location?.search]);

    return queryParams;
};

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        root: {},
        grey600: {
            color: theme.palette.grey[600],
        },
        wrapperLoader: {
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: `calc(100vh - ${theme.footer.height})`,
        },
        sendCheckboxContainer: {
            textAlign: "center",
            margin: "15px 0",
        },
        checkboxControlLabel: {
            "& .MuiTypography-body1": {
                color: `${theme.palette.grey[700]} !important`,
                fontSize: "18px",
            },
        },
        bottomSheetBtn: {
            height: "100%",
            width: "100%",
            paddingTop: "15px",
            color: theme.palette.grey[700],
        },
        checkedIcon: {
            color: theme.palette.grey[700],
        },
        bottomSheetPadding: {
            paddingBottom: "120px",
        },
        dialog: {
            width: "90vw",
        },
        closeBtn: {
            position: "absolute",
            right: 8,
            top: 8,
            color: theme.palette.grey[500],
        },
        dialogContent: {
            marginTop: "20px",
        },
        dialogTypography: {
            textAlign: "center",
            color: theme.palette.grey[600],
            marginBottom: "10px",
        },
        noContent: {
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            position: "fixed",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: "100%",
            color: `${theme.palette.common.black}`,
        },
    }),
);
